import { useEffect, useState } from 'react';
import { tilesByGid } from '../../../game/tileSheet';

const PUBLIC = process.env.PUBLIC_URL || '';

/**
 * Load a world's tileset — the image, and the manifest naming its tiles.
 *
 * LOADED ONCE, IN THE PAGE, and handed to both the palette and the canvas. They
 * draw the same tiles at different sizes, so sharing one decoded `Image` means
 * one network fetch and no chance of the two disagreeing about which sheet they
 * are looking at.
 *
 * THE MANIFEST IS OPTIONAL. It is written by `npm run generate:tileset`, but a
 * tileset downloaded from anywhere else will not have one — and the editor still
 * has to work, showing tile numbers instead of names. So a missing or unparseable
 * manifest is a quiet fallback, never an error.
 *
 * THE IMAGE FAILING IS ALSO NOT FATAL. `ready` stays false and callers fall back
 * to drawing plain blocks, which is the view the editor shipped with. An editor
 * that renders nothing because the art 404'd would be far worse than one that
 * renders squares.
 *
 * @param {{image?: string, columns?: number, firstgid?: number} | null} tileset
 */
export default function useTileSheet(tileset) {
  const [sheet, setSheet] = useState({
    image: null, ready: false, names: new Map(), failed: false,
  });

  const src = tileset?.image;

  useEffect(() => {
    if (!src) return undefined;

    // Cancelled on unmount or when the world changes, so a slow load cannot
    // resolve into a page that has moved on and swap the art underneath it.
    let live = true;
    setSheet((s) => ({ ...s, ready: false, failed: false }));

    const image = new Image();
    image.onload = () => {
      if (live) setSheet((s) => ({ ...s, image, ready: true }));
    };
    image.onerror = () => {
      if (!live) return;
      // eslint-disable-next-line no-console
      console.warn(`[useTileSheet] failed to load tileset "${src}" — falling back to blocks`);
      setSheet((s) => ({
        ...s, image: null, ready: false, failed: true,
      }));
    };
    image.src = `${PUBLIC}/tilesets/${src}`;

    // `.json` beside the `.png`, which is what the generator writes.
    const manifestUrl = `${PUBLIC}/tilesets/${src.replace(/\.png$/i, '')}.tiles.json`;
    fetch(manifestUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => { if (live) setSheet((s) => ({ ...s, names: tilesByGid(m) })); })
      .catch(() => { /* no manifest is normal — the picker shows numbers */ });

    return () => { live = false; };
  }, [src]);

  return sheet;
}
